import axios from "axios";

import { BASE_URL } from "./baseUrls";
import { WALLET_API_URL } from "./urlMetaData";

import {
  InterceptorRequestHandler,
  InterceptorResponseErrorHandler,
  InterceptorResponseHandler,
} from "./InterceptorHandler";
import secureLocalStorage from "react-secure-storage";
import { AUTH_LOCAL_VARIABLES } from "../store/features/authSlice";
import CardService from "./card-service";

// Wallet Service Config
const WalletService = axios.create({ baseURL: BASE_URL.WALLET });

WalletService.interceptors.request.use((req) => InterceptorRequestHandler(req));

// Dynamically, setting baseURL based on active region
WalletService.interceptors.request.use((config) => {
  const REGION = secureLocalStorage.getItem(AUTH_LOCAL_VARIABLES.REGION);
  if (!REGION) return config;
  config.baseURL = BASE_URL[REGION].WALLET;
  return config;
});

WalletService.interceptors.response.use(
  (res) => {
    InterceptorResponseHandler(res);
    return res;
  },
  (error) => {
    InterceptorResponseErrorHandler(error);
    return error;
  }
);

export const getBrokerList = async (clientHashId) => {
  return await WalletService.post("/customer/broker/list", {
    clientHashId: "9ba469ba-567b-48b3-9910-ca7c6c780814",
    page: 0,
    size: 10,
  });
};

export const fetchReportPrefund = async ({
  userHashId = "",
  dateRange = "",
  page = 0,
  size = 10,
  isReport = true,
  status = "",
  filters,
}) => {
  const res = await WalletService.post(
    `${WALLET_API_URL.PREFUND_REQUEST_URL}${
      isReport ? userHashId : "all"
    }/program/all/prefundList`,
    {
      userHashId,
      isReport,
      dateRange: dateRange,
      page,
      size,
      ...filters,
      status,
    }
  );
  return res.data;
};

export const fetchReportLedger = async ({
  dateRange = "",
  page = 0,
  size = 10,
  userHashId = "",
  filters,
}) => {
  const res = await WalletService.post(
    `${WALLET_API_URL.GET_REPORT_LEDGER}/${userHashId}`,
    {
      dateRange: dateRange,
      page,
      size,
      ...filters,
    }
  );
  return res.data;
};

export const fetchReportB2B = async ({
  dateRange = "",
  page = 0,
  size = 10,
  userHashId = "",
  clientHashId = "",
  filters,
  status = "",
  approverUserHashId = "",
}) => {
  const res = await WalletService.post(`${WALLET_API_URL.GET_B2B_REPORT}`, {
    dateRange: dateRange,
    page,
    size,
    approverUserHashId,
    userHashId,
    clientHashId,
    ...filters,
    status,
  });
  return res.data;
};

export const fetchDriverPaymentReq = async ({
  dateRange = "",
  page = 0,
  size = 10,
  userHashId = "",
  userType = "",
  filters,
}) => {
  const res = await WalletService.post(`${WALLET_API_URL.GET_DPREQ_REPORT}`, {
    dateRange: dateRange,
    page,
    size,
    userHashId,
    userType,
    ...filters,
  });
  return res.data;
};

export const fetchDriverPaymentReqList = async ({
  dateRange = "",
  page = 0,
  size = 10,
  clientHashId = "",
  userType = "",
  filters,
  status = "Requested",
}) => {
  const res = await WalletService.post(`${WALLET_API_URL.GET_DPREQ_LIST}`, {
    dateRange: dateRange,
    page,
    size,
    clientHashId,
    userType,
    ...filters,
    status,
  });
  return res.data;
};

export const updateApproveCardLoadRequest = async ({
  status = "",
  userHashId = "",
  referenceNo,
  reason = "",
}) => {
  const res = await WalletService.post(
    `${WALLET_API_URL.UPDATE_CARD_LOAD_STATUS}`,
    {
      status,
      referenceNumber: referenceNo,
      adminUserHashId: userHashId,
      reason,
    }
  );
  return res.data;
};

export const updateApprovePrefundRequest = async ({
  status = "",
  userHashId = "",
  clientHashId = "",
  programHashId = "",
  systemReferenceNumber,
  approvedAmount,
}) => {
  const url = `${WALLET_API_URL.PREFUND_REQUEST_URL}${clientHashId}/program/${programHashId}/updatePrefundRequest`;
  const res = await WalletService.put(url, {
    status,
    systemReferenceNumber,
    isAmountEdited: "true",
    approvedByUserHashId: userHashId,
    approvedAmount,
  });
  return res.data;
};

export const updateB2BRequestStatus = async ({
  status = "",
  userHashId = "",
  referenceNo,
  remarks = "",
}) => {
  const res = await WalletService.post(`${WALLET_API_URL.UPDATE_B2B_STATUS}`, {
    status,
    referenceNumber: referenceNo,
    remarks,
  });
  return res.data;
};

export const updateRequestsDriverPaymentStatus = async ({
  status = "",
  systemReferenceNo,
  ownerUserComment,
  userType,
  approvedAmount,
  cashReceiptVoucher,
  orderId,
  expenseCategory,
}) => {
  const res = await WalletService.post(
    `${WALLET_API_URL.UPDATE_DRIVER_PAYMENT_STATUS}`,
    {
      paymentRequestStatus: status,
      userType,
      approvedAmount,
      systemReferenceNo,
      orderId,
      cashReceiptVoucher,
      ownerUserComment,
      expenseCategory,
    }
  );
  return res.data;
};

export const AllocateDriverRequest = async ({
  name,
  userHashId,
  userType,
  systemReferenceNo,
}) => {
  const res = await WalletService.post(
    `${WALLET_API_URL.ALLOCATE_DRIVER_PAYMENT_REQ}`,
    {
      userType,
      systemReferenceNo,
      name,
      userHashId,
    }
  );
  return res.data;
};

export const GetUserLimits = async ({ userHashId }) => {
  const res = await WalletService.get(
    `${WALLET_API_URL.LOAD_LIMIT_LIST}/${userHashId}`
  );
  return res.data;
};

export const getPaymentReqStats = async ({
  userHashId,
  clientHashId,
  dateRange,
}) => {
  const res = await WalletService.post(
    `${WALLET_API_URL.GET_DRIVER_PAYMENT_REQ_STATS}`,
    {
      userHashId,
      clientHashId,
      dateRange,
    }
  );
  return res.data;
};

export const fetchCardStats = async ({
  userHashId,
  clientHashId,
  programHashId,
  currencyCode,
}) => {
  const res = await WalletService.post(`${WALLET_API_URL.GET_CARDS_STATS}`, {
    userHashId,
    programHashId,
    currencyCode,
    clientHashId,
  });
  return res.data;
};

export const requestPayment = async (formData) => {
  const res = await WalletService.post(
    `${WALLET_API_URL.REQUEST_PAYMENT}`,
    formData
  );
  return res;
};

// Stats
export const fetchGroupPrefund = async ({
  userHashId,
  programHashId,
  currencyCode,
}) => {
  const res = await WalletService.get(
    `${WALLET_API_URL.TOTAL_GROUP_PREFUND}/${userHashId}/${currencyCode}/${programHashId}`
  );
  return res.data;
};

export const fetchAvailableBalance = async ({
  userHashId,
  programHashId,
  currency,
}) => {
  const res = await WalletService.post(
    WALLET_API_URL.GET_AVAILABLE_PREF_BALANCE,
    {
      userHashId,
      programHashId,
      currency,
    }
  );
  return res.data;
};

export const fetchLoadLimit = async ({
  userHashId,
  userType,
  clientHashId,
}) => {
  const res = await WalletService.get(
    `${WALLET_API_URL.LOAD_LIMIT_LIST}/${userHashId}`,
    {
      userType: userType,
      clientHashId: clientHashId,
    }
  );
  return res.data;
};

export const updateTeamBalance = async (payload) => {
  const res = await WalletService.post(
    WALLET_API_URL.UPDATE_TEAM_BALANCE,
    payload
  );
  return res.data;
};

export const updateDriverPayReqsDocs = async (payload) => {
  const res = await WalletService.post(
    WALLET_API_URL.EDIT_DRIVER_PAY_REQS,
    payload
  );
  return res.data;
};

export default WalletService;
